import { Link } from 'react-router-dom';
import { useLiff } from '../contexts/LiffContext';

/**
 * 租戶網站首頁
 * 提供導航連結到各功能頁面
 * 在統一的 LIFF App 中，所有頁面共享 LINE 身份認證
 */
export function HomePage() {
  const { isReady, profile, isLoggedIn, shouldUseLiff } = useLiff();
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* 標題區塊 */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            歡迎來到商家網站
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            這裡是商家的官方網站，提供會員服務、預購功能、問卷調查、取貨服務、購買清單和帳號管理
          </p>
          {shouldUseLiff && isReady && isLoggedIn && profile && (
            <p className="text-sm text-gray-500 mt-2">
              已登入：{profile.displayName}
            </p>
          )}
        </div>

        {/* 第一列：主要功能卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8 mb-8">
          {/* 會員中心卡片 */}
          <Link
            to="/member"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                會員中心
              </h2>
              <p className="text-gray-600 mb-4">
                查看會員資訊、點數記錄和訂單歷史
              </p>
              <div className="text-blue-600 font-semibold">前往 →</div>
            </div>
          </Link>

          {/* 預購功能卡片 */}
          <Link
            to="/preorder"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                預購專區
              </h2>
              <p className="text-gray-600 mb-4">瀏覽預購商品並下單預訂</p>
              <div className="text-blue-600 font-semibold">前往 →</div>
            </div>
          </Link>

          {/* 問卷調查卡片 */}
          <Link
            to="/survey"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                問卷調查
              </h2>
              <p className="text-gray-600 mb-4">填寫問卷，幫助我們改進服務</p>
              <div className="text-blue-600 font-semibold">前往 →</div>
            </div>
          </Link>

          {/* 取貨功能卡片 */}
          <Link
            to="/pickup"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                取貨服務
              </h2>
              <p className="text-gray-600 mb-4">查看待取貨訂單並確認取貨</p>
              <div className="text-blue-600 font-semibold">前往 →</div>
            </div>
          </Link>

          {/* 線上購物卡片 */}
          <Link
            to="/online-shopping"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                線上購物
              </h2>
              <p className="text-gray-600 mb-4">瀏覽商品並線上下單</p>
              <div className="text-blue-600 font-semibold">前往 →</div>
            </div>
          </Link>

          {/* 線上點餐卡片 */}
          <Link
            to="/online-order"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2H9a2 2 0 00-2 2v2m0 4h10m-10 0v4h10v-4"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                線上點餐
              </h2>
              <p className="text-gray-600 mb-4">查看線上點餐狀態與明細</p>
              <div className="text-blue-600 font-semibold">前往 →</div>
            </div>
          </Link>
        </div>

        {/* 第二列：Lite 功能卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8 mb-16">
          {/* Lite 購買清單卡片 */}
          <Link
            to="/lite/hold"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-purple-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                建立購買清單
              </h2>
              <p className="text-gray-600 mb-4">
                建立購買清單，分享給其他人一起參與
              </p>
              <div className="text-purple-600 font-semibold">前往 →</div>
            </div>
          </Link>

          {/* Lite 參與清單卡片 */}
          <Link
            to="/lite/join"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-purple-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                參與購買清單
              </h2>
              <p className="text-gray-600 mb-4">
                加入其他人的購買清單，選擇您要的數量
              </p>
              <div className="text-purple-600 font-semibold">前往 →</div>
            </div>
          </Link>

          {/* Lite 註冊卡片（第二列） */}
          <Link
            to="/lite/register"
            className="bg-white rounded-2xl shadow-md hover:shadow-lg p-8 transition-all duration-300 transform hover:-translate-y-1 border border-purple-100"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full mb-4 shadow-sm">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                註冊帳號
              </h2>
              <p className="text-gray-600 mb-4">註冊新帳號以使用完整功能</p>
              <div className="text-purple-600 font-semibold">前往 →</div>
            </div>
          </Link>
        </div>

        {/* 頁尾資訊 */}
        <div className="text-center text-gray-500 text-sm">
          <p>© 2025 商家網站 - 由 POS 系統提供技術支援</p>
        </div>
      </div>
    </div>
  );
}
